const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function getToken() { return localStorage.getItem('retro_token'); }

function translateError(msg) {
  if (!msg) return 'Something went wrong. Please try again.';
  if (msg.includes('duplicate key') || msg.includes('unique constraint')) {
    if (msg.includes('teams_name_key')) return 'A team with that name already exists.';
    if (msg.includes('sessions')) return 'A session with that name already exists for this team.';
    return 'This item already exists.';
  }
  if (msg.includes('401') || msg.includes('expired')) return 'Your Jira session has expired. Please sign in again.';
  if (msg.includes('429')) return 'Too many requests. Please wait a moment and try again.';
  if (msg.includes('403')) return 'You do not have permission to do that.';
  if (msg.includes('404')) return 'Item not found.';
  if (msg.includes('500')) return 'Something went wrong on the server. Please try again.';
  return msg;
}

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    },
    body: options.body ? JSON.stringify(options.body) : undefined
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(translateError(err.error || `Request failed: ${res.status}`));
  }
  if (res.status === 204) return null;
  return res.json();
}

async function downloadFile(path, filename, body = null) {
  const token = getToken();
  const fetchOpts = {
    method: body ? 'POST' : 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body ? { 'Content-Type': 'application/json' } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  };
  const res = await fetch(`${API_URL}${path}`, fetchOpts);
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = {
  auth: {
    loginUrl: () => `${API_URL}/auth/login`,
    me: () => request('/auth/me'),
    refresh: () => request('/auth/refresh', { method: 'POST' }),
    localLogin: (password, displayName) => request('/auth/local-login', { method: 'POST', body: { password, displayName } })
  },
  teams: {
    list: () => request('/teams'),
    create: (body) => request('/teams', { method: 'POST', body }),
    update: (id, body) => request(`/teams/${id}`, { method: 'PATCH', body }),
    delete: (id) => request(`/teams/${id}`, { method: 'DELETE' }),
    baselines: (id, points) => request(`/teams/${id}/baselines${points != null ? `?points=${points}` : ''}`),
    addBaseline: (id, body) => request(`/teams/${id}/baselines`, { method: 'POST', body }),
    deleteBaseline: (tid, bid) => request(`/teams/${tid}/baselines/${bid}`, { method: 'DELETE' })
  },
  sessions: {
    create: (body) => request('/sessions', { method: 'POST', body }),
    get: (id) => request(`/sessions/${id}`),
    join: (id, body) => request(`/sessions/${id}/join`, { method: 'POST', body }),
    phase: (id, phase) => request(`/sessions/${id}/phase`, { method: 'PATCH', body: { phase } }),
    reveal: (id) => request(`/sessions/${id}/reveal`, { method: 'PATCH' }),
    lock: (id, locked) => request(`/sessions/${id}/lock`, { method: 'PATCH', body: { locked } }),
    timer: (id, action) => request(`/sessions/${id}/timer`, { method: 'PATCH', body: { action } }),
    transferFacilitator: (id, participantId) => request(`/sessions/${id}/transfer-facilitator`, { method: 'PATCH', body: { new_facilitator_participant_id: participantId } }),
    end: (id) => request(`/sessions/${id}/end`, { method: 'PATCH' }),
    votes: (id) => request(`/sessions/${id}/votes`),
    previousCommitments: (id) => request(`/sessions/${id}/previous-commitments`),
    updateParticipantName: (sessionId, participantId, body) => request(`/sessions/${sessionId}/participants/${participantId}/name`, { method: 'PATCH', body })
  },
  cards: {
    create: (body) => request('/cards', { method: 'POST', body }),
    update: (id, body) => request(`/cards/${id}`, { method: 'PATCH', body }),
    delete: (id, body) => request(`/cards/${id}`, { method: 'DELETE', body }),
    merge: (body) => request('/cards/merge', { method: 'POST', body }),
    ungroup: (id) => request(`/cards/${id}/ungroup`, { method: 'PATCH' }),
    updateGroupLabel: (groupId, label, sessionId) => request(`/cards/group/${groupId}/label`, { method: 'PATCH', body: { label, session_id: sessionId } })
  },
  actions: {
    create: (body) => request('/actions', { method: 'POST', body }),
    approve: (id, body) => request(`/actions/${id}/approve`, { method: 'PATCH', body }),
    dismiss: (id, reason) => request(`/actions/${id}/dismiss`, { method: 'PATCH', body: { reason } }),
    update: (id, body) => request(`/actions/${id}`, { method: 'PATCH', body }),
    vote: (id, body) => request(`/actions/${id}/vote`, { method: 'POST', body }),
    unvote: (id, body) => request(`/actions/${id}/vote`, { method: 'DELETE', body }),
    aiAnalyze: (sessionId) => request('/actions/ai-analyze', { method: 'POST', body: { session_id: sessionId } }),
    createJira: (id) => request(`/actions/${id}/create-jira`, { method: 'POST' }),
    generateSummary: (sessionId) => request('/actions/generate-summary', { method: 'POST', body: { session_id: sessionId } }),
    slackExport: (sessionId, extra = {}) => request('/actions/slack-export', { method: 'POST', body: { session_id: sessionId, ...extra } }),
    carryover: (teamId, sessionId) => request(`/actions/carryover?team_id=${teamId}&current_session_id=${sessionId}`),
    setOutcome: (id, outcome) => request(`/actions/${id}/outcome`, { method: 'PATCH', body: { outcome } }),
    carryForward: (id, currentSessionId) => request(`/actions/${id}/carry-forward`, { method: 'POST', body: { current_session_id: currentSessionId } })
  },
  jira: {
    reestimate: (projectKey) => request(`/jira/reestimate?projectKey=${projectKey}`),
    markReviewed: (key) => request(`/jira/tickets/${key}/mark-reviewed`, { method: 'PATCH' }),
    markBaseline: (key, body) => request(`/jira/tickets/${key}/mark-baseline`, { method: 'PATCH', body })
  },
  reports: {
    sessions: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/reports/sessions${q ? `?${q}` : ''}`); },
    session: (id) => request(`/reports/sessions/${id}`),
    exportPdfUrl: (id) => `${API_URL}/reports/sessions/${id}/export/pdf`,
    exportCsvUrl: (id) => `${API_URL}/reports/sessions/${id}/export/csv`,
    downloadPdf: (id) => downloadFile(`/reports/sessions/${id}/export/pdf`, `retro-${id}.html`),
    downloadCsv: (id) => downloadFile(`/reports/sessions/${id}/export/csv`, `retro-${id}.csv`),
    downloadConfluence: (id) => downloadFile(`/reports/sessions/${id}/export/confluence`, `retro-${id}-confluence.html`)
  },
  formats: {
    list: () => request('/formats'),
    getPhases: (formatId) => request(`/formats/${formatId}/phases`),
  },
  templates: {
    list: () => request('/templates'),
    create: (data) => request('/templates', { method: 'POST', body: data }),
    delete: (id) => request(`/templates/${id}`, { method: 'DELETE' }),
  },
  teamDashboard: {
    get: (teamId) => request(`/team-dashboard/${teamId}/dashboard`),
  },
  notes: {
    get: (sessionId) => request(`/sessions/${sessionId}/notes`),
    save: (sessionId, notes) => request(`/sessions/${sessionId}/notes`, { method: 'PATCH', body: { notes } }),
  },
  actionReviews: {
    submit: (sessionId, actionId, outcome, note) => request(`/sessions/${sessionId}/review-action`, {
      method: 'POST',
      body: { action_id: actionId, outcome, note }
    }),
  }
};
